import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { isEmpty, isNil } from 'lodash'

import NoData from '@/components/NoData'
import { Loader } from '@/components/loaders/Loader'
import { formatDuration, formatTime } from '@/utils/helpers'
import {
  fetchRecentCallsForConfig,
  RECENT_CALLS_LIMIT,
} from '@/reducers/playbooks/playbook.actions'

// The reporting API sends start times without an offset, so they are read as UTC
// and shown in the viewer's zone — `new Date(...)` would treat them as local and
// shift every row. A call with no start time says so rather than rendering as
// the epoch. Moment tokens, since that is what formatTime speaks; the year is
// dropped because the list only ever reaches back 30 days.
const CALL_TIME_FORMAT = 'MMM D, h:mm A'

/**
 * The interactions this configuration has already run on, newest first. Picking
 * one fills in the Call ID field, so a test can be started without leaving the
 * page to find an id.
 *
 * @param {string} props.playbookCid The configuration's playbook, or empty for an unscoped channel flow.
 * @param {string} props.callType The channel the configuration runs on.
 * @param {number} props.organizationId The org the calls belong to (an admin's selection, or the user's own).
 * @param {string} props.selectedCallId The call id currently in the field, so its row reads as chosen.
 * @param {Function} props.onSelect Called with the picked call's id.
 */
export const RecentCallsList = ({
  playbookCid,
  callType,
  organizationId,
  selectedCallId,
  onSelect,
}) => {
  const dispatch = useDispatch()
  const { rtnRecentCalls = [], loading } = useSelector((state) => state.playbook)

  useEffect(() => {
    dispatch(fetchRecentCallsForConfig({ playbookCid, callType, organizationId }))
  }, [playbookCid, callType, organizationId])

  if (loading?.rtnRecentCalls) {
    return (
      <div className="recent-calls__loading" data-testid="recent-calls-loader">
        <Loader />
      </div>
    )
  }

  return (
    <div className="recent-calls" data-testid="recent-calls">
      <h5 className="recent-calls__title">Last {RECENT_CALLS_LIMIT} calls</h5>
      {isEmpty(rtnRecentCalls) ? (
        <NoData
          muted
          padded
          data-testid="recent-calls-empty"
          headerText="No recent calls"
          text="Nothing has run through this configuration in the last 30 days. Enter a call id above to test against an older one."
        />
      ) : (
        rtnRecentCalls.map((call) => (
          <button
            key={call.call_id}
            type="button"
            className={classNames('recent-call', {
              selected: String(call.call_id) === String(selectedCallId),
            })}
            data-testid={`recent-call-${call.call_id}`}
            onClick={() => onSelect(String(call.call_id))}
          >
            <div className="recent-call__headline">
              <span className="recent-call__name">{call.agent_name || `Call ${call.call_id}`}</span>
              <span className="recent-call__time">
                {isNil(call.start_time)
                  ? 'No start time'
                  : formatTime(call.start_time, CALL_TIME_FORMAT)}
              </span>
            </div>
            <div className="recent-call__meta">
              {[
                call.playbook_name,
                // Messages and emails have no duration to report.
                Number.isFinite(call.duration) ? formatDuration(call.duration) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </button>
        ))
      )}
    </div>
  )
}
